import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import DeviceScreenLayout from "../../components/layouts/DeviceScreenLayout";
import Header from "../../components/layouts/Header";

import React, { useEffect, useMemo, useState } from "react";
import { getReservations } from "../../api/reservations";
import { getStoreChannels } from "../../api/stores";
import { useAuth } from "../../contexts/AuthContext";

const PRIMARY = '#477FEE';
const TODAY = new Date();
const TODAY_STR = `${TODAY.getFullYear()}-${String(TODAY.getMonth() + 1).padStart(2, '0')}-${String(TODAY.getDate()).padStart(2, '0')}`;

const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];
const COLORS = ['#477FEE', '#2ECC71', '#FF9F43', '#E74C3C', '#9B59B6'];

const fmtTime = (dt) => dt?.slice(11, 16) ?? '';

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}월 ${d.getDate()}일 (${['일','월','화','수','목','금','토'][d.getDay()]})`;
};

const MAX_VISIBLE = 2;

const CalendarCell = ({ day, dateStr, isSelected, isToday, onClick, events, calendarColors }) => {
    const [hovered, setHovered] = useState(false);
    const empty = !day;
    const visible = (events || []).slice(0, MAX_VISIBLE);
    const overflow = (events || []).length - MAX_VISIBLE;

    return (
        <div
            onClick={() => !empty && onClick(dateStr)}
            onMouseEnter={() => !empty && setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex',
                flexDirection: 'column',
                paddingTop: 6,
                paddingBottom: 4,
                paddingLeft: 4,
                paddingRight: 4,
                cursor: empty ? 'default' : 'pointer',
                borderRadius: 8,
                background: isSelected ? PRIMARY : hovered && !empty ? '#F0F5FF' : 'transparent',
                transition: 'background 0.12s',
                userSelect: 'none',
                overflow: 'hidden',
                minHeight: 0,
            }}
        >
            {/* 날짜 숫자 */}
            <div style={{
                width: 26,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: isToday && !isSelected ? `2px solid ${PRIMARY}` : 'none',
                flexShrink: 0,
                marginBottom: 3,
            }}>
                <span style={{
                    fontSize: 13,
                    fontFamily: 'Pretendard',
                    fontWeight: isSelected || isToday ? 700 : 500,
                    color: isSelected ? '#fff' : isToday ? PRIMARY : empty ? 'transparent' : '#1A1E27',
                }}>
                    {day || ''}
                </span>
            </div>

            {/* 이벤트 칩 */}
            {!empty && visible.map(ev => {
                const color = calendarColors[ev.calendar_name] ?? PRIMARY;
                const chipColor = isSelected ? 'rgba(255,255,255,0.25)' : `${color}20`;
                const borderColor = isSelected ? 'rgba(255,255,255,0.7)' : color;
                const textColor = isSelected ? '#fff' : '#1A1E27';
                return (
                    <div key={ev.event_id} style={{
                        borderLeft: `3px solid ${borderColor}`,
                        background: chipColor,
                        borderRadius: '0 3px 3px 0',
                        padding: '1px 4px',
                        marginBottom: 2,
                        fontSize: 9,
                        fontFamily: 'Pretendard',
                        color: textColor,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.6,
                        flexShrink: 0,
                    }}>
                        {fmtTime(ev.start.dateTime)}-{fmtTime(ev.end.dateTime)} {ev.summary}
                    </div>
                );
            })}
            {!empty && overflow > 0 && (
                <div style={{
                    fontSize: 9,
                    fontFamily: 'Pretendard',
                    color: isSelected ? 'rgba(255,255,255,0.8)' : '#8A94A6',
                    paddingLeft: 4,
                    lineHeight: 1.6,
                    flexShrink: 0,
                }}>
                    +{overflow}개
                </div>
            )}
        </div>
    );
};

const Calendar = ({ year, month, selectedDate, setSelectedDate, eventsByDate, calendarColors }) => {
    const firstDayOfMonth = new Date(year, month - 1, 1);
    const lastDayOfMonth = new Date(year, month, 0).getDate();
    const startDay = firstDayOfMonth.getDay();

    const days = Array.from({ length: 42 }).map((_, i) => {
        const d = i - startDay + 1;
        return d > 0 && d <= lastDayOfMonth ? d : null;
    });

    const fm = String(month).padStart(2, '0');

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* 요일 헤더 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4, flexShrink: 0 }}>
                {DAY_LABELS.map((label, i) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
                        <span style={{
                            fontSize: 11,
                            fontFamily: 'Pretendard',
                            fontWeight: 600,
                            color: i === 0 ? '#FF5F67' : i === 6 ? PRIMARY : '#8A94A6',
                            letterSpacing: '-0.3px',
                        }}>
                            {label}
                        </span>
                    </div>
                ))}
            </div>

            {/* 날짜 그리드 */}
            <div style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gridTemplateRows: 'repeat(6, 1fr)',
                gap: 2,
                minHeight: 0,
            }}>
                {days.map((day, i) => {
                    const dateStr = day ? `${year}-${fm}-${String(day).padStart(2, '0')}` : null;
                    return (
                        <CalendarCell
                            key={i}
                            day={day}
                            dateStr={dateStr}
                            isSelected={day && selectedDate === dateStr}
                            isToday={day && dateStr === TODAY_STR}
                            onClick={setSelectedDate}
                            events={dateStr ? (eventsByDate[dateStr] || []) : []}
                            calendarColors={calendarColors}
                        />
                    );
                })}
            </div>
        </div>
    );
};

const CalendarView = ({ year, month, handlePrevMonth, handleNextMonth, selectedDate, setSelectedDate, eventsByDate, calendarColors }) => (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 16, minHeight: 0 }}>
        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 22, fontFamily: 'Pretendard', fontWeight: 700, color: '#1A1E27' }}>{year}년</span>
                <span style={{ fontSize: 22, fontFamily: 'Pretendard', fontWeight: 700, color: PRIMARY }}>{month}월</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                {[{ icon: <BiChevronLeft size={18} />, fn: handlePrevMonth }, { icon: <BiChevronRight size={18} />, fn: handleNextMonth }].map(({ icon, fn }, i) => (
                    <button
                        key={i}
                        onClick={fn}
                        style={{
                            width: 32, height: 32,
                            border: '1.5px solid #E5E8EE',
                            borderRadius: 8,
                            background: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer',
                            color: '#8A94A6',
                        }}
                    >
                        {icon}
                    </button>
                ))}
            </div>
        </div>

        <Calendar
            year={year}
            month={month}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            eventsByDate={eventsByDate}
            calendarColors={calendarColors}
        />

        {/* 레전드 */}
        {Object.keys(calendarColors).length > 0 && (
            <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
                {Object.entries(calendarColors).map(([name, color]) => (
                    <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
                        <span style={{ fontSize: 11, fontFamily: 'Pretendard', color: '#8A94A6' }}>{name}</span>
                    </div>
                ))}
            </div>
        )}
    </div>
);

const TimeSlot = React.memo(({ index, slotDuration, reservedInfo }) => {
    const slotStartMin = index * slotDuration;
    const hours = Math.floor(slotStartMin / 60);
    const minutes = slotStartMin % 60;
    const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    const isReserved = !!reservedInfo;
    const showHour = minutes === 0;

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            minHeight: slotDuration <= 10 ? 22 : 32,
            padding: '0 12px',
            borderTop: showHour ? '1px solid #E5E8EE' : '1px solid #F5F6F8',
            background: isReserved ? '#EEF4FF' : '#fff',
        }}>
            <span style={{
                width: 44,
                fontSize: 10,
                fontFamily: 'Pretendard',
                fontWeight: showHour ? 600 : 400,
                color: showHour ? '#8A94A6' : '#C5CAD4',
                flexShrink: 0,
            }}>
                {showHour ? timeStr : ''}
            </span>

            <div style={{ flex: 1, fontSize: 11, fontFamily: 'Pretendard' }}>
                {isReserved ? (
                    <span style={{ color: PRIMARY, fontWeight: 600 }}>
                        {reservedInfo.customer_name}
                        {reservedInfo.service_name ? (
                            <span style={{ fontWeight: 400, color: '#8A94A6' }}> · {reservedInfo.service_name}</span>
                        ) : null}
                    </span>
                ) : null}
            </div>
        </div>
    );
});

const ChannelPicker = ({ channels, pickedChannel, onPick }) => (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 0 4px' }}>
        {channels.map(ch => {
            const active = pickedChannel?.uuid === ch.uuid;
            return (
                <button
                    key={ch.uuid}
                    onClick={() => onPick(ch)}
                    style={{
                        padding: '6px 14px',
                        borderRadius: 20,
                        border: active ? `1.5px solid ${PRIMARY}` : '1.5px solid #E5E8EE',
                        background: active ? '#EEF4FF' : '#fff',
                        color: active ? PRIMARY : '#8A94A6',
                        fontFamily: 'Pretendard',
                        fontSize: 13,
                        fontWeight: active ? 700 : 500,
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                        letterSpacing: '-0.2px',
                    }}
                >
                    {ch.name}
                </button>
            );
        })}
    </div>
);

const ReservationsPage = () => {
    const { selectedStore, selectedChannel } = useAuth();
    const storeUuid = selectedStore?.uuid;

    const [channels, setChannels] = useState([]);
    const [pickedChannel, setPickedChannel] = useState(selectedChannel ?? null);

    const now = new Date();
    const [viewMonth, setViewMonth] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
    const [selectedDate, setSelectedDate] = useState(TODAY_STR);
    const [slotDuration] = useState(5);
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!storeUuid) return;
        getStoreChannels(storeUuid).then(list => {
            setChannels(list);
            setPickedChannel(prev => prev ?? list[0] ?? null);
        }).catch(() => {});
    }, [storeUuid]);

    const handleNextMonth = () => setViewMonth(prev =>
        prev.month === 12 ? { year: prev.year + 1, month: 1 } : { ...prev, month: prev.month + 1 }
    );
    const handlePrevMonth = () => setViewMonth(prev =>
        prev.month === 1 ? { year: prev.year - 1, month: 12 } : { ...prev, month: prev.month - 1 }
    );

    const getTotalSlots = (d) => Math.floor(1440 / d);

    useEffect(() => {
        if (!storeUuid || !pickedChannel?.uuid) return;
        const { year, month } = viewMonth;
        const fm = String(month).padStart(2, '0');
        const lastDay = new Date(year, month, 0).getDate();
        const startDate = `${year}-${fm}-01`;
        const endDate = `${year}-${fm}-${String(lastDay).padStart(2, '0')}`;
        const fetch = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await getReservations(storeUuid, pickedChannel.uuid, startDate, endDate);
                if (result.success) setReservations(result.data.events);
            } catch {
                setError("예약 데이터를 불러오는 데 실패했습니다.");
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [storeUuid, pickedChannel?.uuid, viewMonth]);

    const calendarColors = useMemo(() => {
        const map = {};
        let idx = 0;
        (reservations || []).forEach(ev => {
            if (ev.calendar_name && !map[ev.calendar_name]) {
                map[ev.calendar_name] = COLORS[idx % COLORS.length];
                idx++;
            }
        });
        return map;
    }, [reservations]);

    const eventsByDate = useMemo(() => {
        const map = {};
        (reservations || []).forEach(ev => {
            const date = ev.start?.dateTime?.slice(0, 10);
            if (!date) return;
            if (!map[date]) map[date] = [];
            map[date].push(ev);
        });
        Object.values(map).forEach(arr =>
            arr.sort((a, b) => a.start.dateTime.localeCompare(b.start.dateTime))
        );
        return map;
    }, [reservations]);

    const reservationsMap = useMemo(() => {
        const map = {};
        (eventsByDate[selectedDate] || []).forEach(ev => {
            const startMin = parseInt(ev.start.dateTime.slice(11, 13)) * 60 + parseInt(ev.start.dateTime.slice(14, 16));
            const endMin = parseInt(ev.end.dateTime.slice(11, 13)) * 60 + parseInt(ev.end.dateTime.slice(14, 16));
            for (let i = startMin; i < endMin; i += slotDuration) {
                map[i] = { customer_name: ev.summary, service_name: ev.description };
            }
        });
        return map;
    }, [eventsByDate, selectedDate, slotDuration]);

    return (
        <DeviceScreenLayout>
            <Header isBackEnabled={true} />

            <div style={{
                height: 'calc(100% - 60px)',
                display: 'flex',
                gap: 0,
                background: '#F3F4F6',
            }}>
                {/* 좌측: 달력 */}
                <div style={{
                    flex: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '28px 24px 24px',
                    minWidth: 0,
                }}>
                    <div style={{
                        flex: 1,
                        background: '#fff',
                        borderRadius: 16,
                        padding: 24,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        gap: 16,
                    }}>
                        {channels.length > 1 && (
                            <ChannelPicker
                                channels={channels}
                                pickedChannel={pickedChannel}
                                onPick={setPickedChannel}
                            />
                        )}
                        <CalendarView
                            year={viewMonth.year}
                            month={viewMonth.month}
                            handlePrevMonth={handlePrevMonth}
                            handleNextMonth={handleNextMonth}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            eventsByDate={eventsByDate}
                            calendarColors={calendarColors}
                        />
                    </div>
                </div>

                {/* 우측: 타임슬롯 */}
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '28px 24px 24px 0',
                    minWidth: 0,
                }}>
                    <div style={{
                        flex: 1,
                        background: '#fff',
                        borderRadius: 16,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}>
                        {/* 패널 헤더 */}
                        <div style={{
                            padding: '16px 16px 12px',
                            borderBottom: '1px solid #F0F1F4',
                            flexShrink: 0,
                        }}>
                            <span style={{
                                fontSize: 14,
                                fontFamily: 'Pretendard',
                                fontWeight: 700,
                                color: '#1A1E27',
                                letterSpacing: '-0.3px',
                            }}>
                                {formatDate(selectedDate)}
                            </span>
                            {loading && (
                                <span style={{ marginLeft: 8, fontSize: 11, color: '#8A94A6', fontFamily: 'Pretendard' }}>불러오는 중...</span>
                            )}
                            {error && (
                                <span style={{ marginLeft: 8, fontSize: 11, color: '#FF5F67', fontFamily: 'Pretendard' }}>{error}</span>
                            )}
                        </div>

                        {/* 슬롯 목록 */}
                        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                            {getTotalSlots(slotDuration) > 0 && [...Array(getTotalSlots(slotDuration))].map((_, i) => (
                                <TimeSlot
                                    key={i}
                                    index={i}
                                    slotDuration={slotDuration}
                                    reservedInfo={reservationsMap[i * slotDuration]}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </DeviceScreenLayout>
    );
};

export default ReservationsPage;
